import React, { useState, useEffect } from 'react'
import { T, Spinner, Avatar, probColor, tdColor, inGameFilter, PosFilter, POS_GROUPS, posOf, GameLogos } from '../ui'
import { getNFLScorers } from '../api'
import { teamNick } from '../plain'

// Scorers board — every game's most-likely anytime-TD scorers, model-ranked. Filter three ways:
// By game (grouped per matchup), Overall (one ranked list across the whole slate), or By team
// (tap a team). One /nfl/scorers fetch powers all three.
const _ord = n => { const s = { 1: 'st', 2: 'nd', 3: 'rd' }[n < 20 ? n : n % 10] || 'th'; return `${n}${s}` }

function ScorerRow({ s, rank, onPlayer }) {
  const pct = Math.round((s.prob || 0) * 100)
  const soft = s.td_soft_rank
  return (
    <button onClick={() => onPlayer && onPlayer({ gsis_id: s.gsis_id, full_name: s.name, team: s.team, pos: s.pos, opp: s.opp, prop_type: 'anytime_td' })}
      style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 10, textAlign: 'left', cursor: 'pointer',
        background: T.card, border: `1px solid ${rank && rank <= 3 ? T.greenMid : T.border}`, borderRadius: 11, padding: '9px 11px', marginBottom: 7, color: T.text }}>
      {rank != null && <span style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 12, color: rank <= 3 ? T.green : T.faint, width: 18, textAlign: 'right', flexShrink: 0 }}>{rank}</span>}
      <Avatar url={s.headshot} name={s.name} team={s.team} size={30} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, lineHeight: 1.2 }}>
          {s.name} <span style={{ fontSize: 10, color: T.muted, fontWeight: 700 }}>{s.pos}</span></div>
        <div style={{ fontSize: 10.5, color: T.muted, marginTop: 1 }}>
          {teamNick(s.team)} vs {teamNick(s.opp)}{soft ? ` · ${_ord(soft)} softest to ${s.pos}` : ''}</div>
      </div>
      <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 16, color: tdColor(s.prob || 0), minWidth: 42, textAlign: 'right' }}>{pct}%</div>
    </button>
  )
}

export default function ScorersTab({ season = 2026, week = 1, onPlayer, gameFilter }) {
  const [d, setD] = useState(null)
  const [scope, setScope] = useState('game')   // 'game' | 'all' | '<TEAM>'
  const [pos, setPos] = useState('ALL')
  useEffect(() => { let a = true; setD(null)
    getNFLScorers(season, week, 10).then(r => { if (a) setD(r) }).catch(() => { if (a) setD({ games: [] }) })
    return () => { a = false } }, [season, week])
  if (!d) return <div style={{ padding: 20 }}><Spinner /></div>
  const games = (d.games || []).filter(g => inGameFilter(gameFilter, g.away) && inGameFilter(gameFilter, g.home))
  const posOk = s => pos === 'ALL' || posOf(s) === pos
  const everyone = games.flatMap(g => [...(g.away_scorers || []), ...(g.home_scorers || [])])
  const counts = Object.fromEntries(POS_GROUPS.map(g => [g, g === 'ALL' ? everyone.length : everyone.filter(s => posOf(s) === g).length]))
  const allScorers = everyone.filter(posOk)
  const teams = [...new Set(everyone.map(s => s.team).filter(Boolean))].sort()

  const chip = (on) => ({ flexShrink: 0, padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
    fontWeight: 800, border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card, color: on ? T.green : T.muted })

  let body
  if (scope === 'all') {
    const ranked = [...allScorers].sort((a, b) => (b.prob || 0) - (a.prob || 0)).slice(0, 40)
    body = ranked.length ? ranked.map((s, i) => <ScorerRow key={`${s.gsis_id}:${i}`} s={s} rank={i + 1} onPlayer={onPlayer} />)
      : <div style={{ color: T.muted, fontSize: 12.5, textAlign: 'center', padding: '24px 0' }}>No scorers yet this week.</div>
  } else if (scope !== 'game') {   // a team filter
    const ranked = allScorers.filter(s => s.team === scope).sort((a, b) => (b.prob || 0) - (a.prob || 0))
    body = ranked.map((s, i) => <ScorerRow key={`${s.gsis_id}:${i}`} s={s} rank={i + 1} onPlayer={onPlayer} />)
  } else {   // by game
    body = games.map(g => {
      const rows = [...(g.away_scorers || []), ...(g.home_scorers || [])].filter(posOk).sort((a, b) => (b.prob || 0) - (a.prob || 0)).slice(0, 8)
      if (!rows.length) return null
      return (
        <div key={g.game_id} style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 2px 8px' }}>
            <GameLogos away={g.away} home={g.home} size={20} names color={T.text} />
            {g.total != null && <span style={{ fontSize: 10.5, color: T.faint, fontFamily: T.mono }}>O/U {g.total}</span>}
          </div>
          {rows.map((s, i) => <ScorerRow key={`${s.gsis_id}:${i}`} s={s} rank={i + 1} onPlayer={onPlayer} />)}
        </div>
      )
    })
  }

  return (
    <div style={{ padding: '12px 16px 80px' }}>
      <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5, marginBottom: 10 }}>
        Every game's <b style={{ color: T.text }}>most likely to score</b> (anytime TD), model-ranked. Filter by game, the whole slate, or one team.
      </div>
      <div style={{ display: 'flex', gap: 7, marginBottom: 12, overflowX: 'auto', paddingBottom: 4 }}>
        <button onClick={() => setScope('game')} style={chip(scope === 'game')}>By game</button>
        <button onClick={() => setScope('all')} style={chip(scope === 'all')}>Overall</button>
        <span style={{ width: 1, background: T.border, flexShrink: 0, margin: '2px 2px' }} />
        {teams.map(t => <button key={t} onClick={() => setScope(t)} style={chip(scope === t)}>{teamNick(t)}</button>)}
      </div>
      {everyone.length > 0 && <PosFilter value={pos} onChange={setPos} counts={counts} />}
      <div style={{ height: 8 }} />
      {body}
    </div>
  )
}
